import React, { useEffect, useState, useRef, useCallback, FormEvent } from 'react';
import { useToast } from 'src/context/ToastProvider';
import { useSearchParams } from 'react-router-dom';
import { Button, Label } from 'flowbite-react';
import Container from 'src/components/shared/Container';
import { BsCloudUploadFill } from 'react-icons/bs';
import { FaArrowLeft } from 'react-icons/fa6';
import CreatableSelect from 'react-select/creatable';
import { getUserPreferenceUIConfig, stringAvatar, validateEmailAddress } from 'shared-utils';
import { MultiValue } from 'react-select';
import { useCSVDownloader } from 'react-papaparse';
import { useMutation } from 'react-query';
import { copyHiringAssessmentLink, inviteAssessementBulkAction } from 'src/apis/hiringAssessment';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { TechStackType, UIHiringAssessment } from 'types';
import { useGetHiringAssessment } from 'src/hooks/hiringAssessment.hooks';
import { useUserContext } from 'src/context/UserProvider';

import { Avatar, useMediaQuery } from '@mui/material';
import InterviewScheduledModal from './Modals/InterviewScheduledModal';
import { Drawer } from 'src/components/shared/Drawer';

import ReactQuill, { Quill } from 'react-quill';

const DropdownIndicator = null;
const IndicatorSeparator = null;
const ShareHiringAssessment = () => {
	const textRef = useRef(null);
	const [searchParams] = useSearchParams();
	const { user } = useUserContext();
	const isMobile = useMediaQuery('(max-width: 960px)');
	const { CSVDownloader } = useCSVDownloader();

	const hiringAssessmentId = searchParams.get('hiringAssessmentId');
	const hiringAssessmentUrl = `https://levelup.masaischool.com/jobseeker/placed-hiring-assessment/${hiringAssessmentId}`;
	const toast = useToast();
	const [files, setFiles] = React.useState<File[]>([]);
	const [candidateAction, setCandidateAction] = useState<boolean>(false);
	const [inputValue, setInputValue] = useState<string>('');

	const [dragActive, setDragActive] = useState(false);
	const [value, setValue] = useState<MultiValue<{ label: string; value: string }>>([]);
	const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
	const [hiringAssessment, setHiringAssessment] = useState<UIHiringAssessment>();
	const [avatarImg, setAvatarImg] = useState('');
	const [InterviewScheduleDrawer, setInterviewScheduleDrawer] = useState(false);
	const [CustomizedEmailDrawer, setCustomizedEmailDrawer] = useState(false);
	const [EmailCount, setEmailCount] = useState(0);
	const [iseditableContent, setIsEditableContent] = useState(false);

	const [companyRole, setCompanyRole] = useState('');

	const generateAvatar = useCallback(async () => {
		if (user?.registeredCompany?.logo) {
			const res = await generateCompanyLogoAvatarSrc(
				user?.registeredCompany?.logo ?? '',
				user?.registeredCompany?.url ?? '',
			);

			setAvatarImg(res);
		} else if (user?.registeredCompany) {
			const res = await generateCompanyLogoAvatarSrc(
				user?.registeredCompany?.logo ?? '',
				user?.registeredCompany?.url ?? '',
			);

			setAvatarImg(res);
		}
	}, [user?.registeredCompany?.logo]);

	const bulkActionFn = useMutation(['inviteAssessementBulkAction'], inviteAssessementBulkAction, {
		onSuccess: () => {
			setInterviewScheduleDrawer(true);
			setCustomizedEmailDrawer(false);
			toast({
				title: 'Invites sent successfully!',
				type: 'success',
			});
		},
		onError: (error: Error) => {
			toast({
				title: 'Error sending invites',
				type: 'error',
			});
		},
	});

	const copyHiringAssessmentLinkFn = useMutation(['copyHiringAssessmentLink'], copyHiringAssessmentLink, {
		onSuccess: () => {
			console.log('Link copied successfully');
		},
	});

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files[0].size <= 2 * 1024 * 1024) {
			const newFiles = event.target.files;
			setFiles([newFiles[0]]);
			parseCSV(newFiles[0]);
		} else {
			alert('File size exceeds 2MB');
		}
	};
	const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		setDragActive(true);
	};

	const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		setDragActive(false);
	};

	const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		setDragActive(false);
		const newFiles = Array.from(event.dataTransfer.files);
		if (newFiles.length === 1 && newFiles[0].size <= 2 * 1024 * 1024) {
			setFiles(newFiles);
			parseCSV(newFiles[0]);
		} else {
			alert('Please drop a single file that is less than or equal to 2MB in size');
		}
	};
	const parseCSV = (file: File) => {
		const reader = new FileReader();
		reader.onload = event => {
			if (event.target && event.target.result) {
				const csvData = event.target.result as string;
				const rows = csvData.split('\n');
				const emails = rows.map(row => row.split(',')[0].trim()).filter(email => validateEmailAddress(email));
				const emailObjects = emails.map(email => ({ label: email, value: email }));
				setValue(prevValue => [...emailObjects]);
			}
		};
		reader.readAsText(file);
	};
	const handleRemoveFile = (index: number) => {
		setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
		// setCandidateAction(true);
	};
	const handleCandidateAction = () => {
		setCandidateAction(!candidateAction);
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === 'Tab') {
			event.preventDefault();
			if (inputValue) {
				const newEmails = inputValue
					.split(/[ ,]+/)
					.map(email => email.trim())
					.filter(email => email);
				const emailObjects = newEmails.map(email => ({ label: email, value: email }));
				setValue([...value, ...emailObjects]);
				setInputValue('');
			}
		}
	};

	const copyUrlToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(hiringAssessmentUrl);
			toast({
				title: 'Link copied successfully!',
				type: 'success',
			});
		} catch (err) {
			toast({
				title: 'Error copying link',
				type: 'error',
			});
		}
	};
	const handleInputTextChange = (newValue: string) => {
		if (!newValue) return;

		// Split the newValue by spaces or commas
		const emailAddresses = newValue.split(/[\s,]+/).filter(Boolean);

		emailAddresses.forEach(email => {
			if (validateEmailAddress(email) && !value.some(item => item?.value === email)) {
				// If the email is valid and not already in the array, add it
				setValue(prev => {
					return [...prev, { label: email, value: email }];
				});
			} else if (!validateEmailAddress(email)) {
				setEmailErrorMessage('Please enter a valid email address');
			} else {
				setEmailErrorMessage('');
			}
		});

		setInputValue('');
	};
	const handleChange = (newValue: MultiValue<{ label: string; value: string }>, event: any) => {
		setValue(newValue);
	};

	useEffect(() => {
		if (value.length <= 0) {
			setFiles([]);
		}
	}, [value]);

	const sampleData = [
		{ email: 'test1@gmail.com' },
		{ email: 'test2@gmail.com' },
		{ email: 'test3@gmail.com' },
		{ email: 'test4@gmail.com' },
		{ email: 'test5@gmail.com' },
		{ email: 'test6@gmail.com' },
	];

	const { data: hiringAssessmentData, isLoading: hiringAssessmentLoading } = useGetHiringAssessment({
		hiringAssessmentId: hiringAssessmentId || '',
		token: localStorage.getItem('guestUserJwtToken') || '',
	});
	useEffect(() => {
		if (hiringAssessmentData) {
			setHiringAssessment(hiringAssessmentData?.hiringAssessment);
		}
	}, [hiringAssessmentData]);
	useEffect(() => {
		if (hiringAssessment) {
			const roleFromAssessment = (getUserPreferenceUIConfig({
				text: hiringAssessment?.userPreferenceSnapshot?.careerPath || '',
			})?.finalText || '') as string;

			if (Array.isArray(roleFromAssessment)) {
				setCompanyRole(roleFromAssessment.join(' '));
			} else {
				setCompanyRole(roleFromAssessment);
			}
		}
	}, [hiringAssessment]);

	useEffect(() => {
		generateAvatar();
	}, [user?.registeredCompany?.logo, user?.registeredCompany?.url]);

	useEffect(() => {
		if (InterviewScheduleDrawer) {
			const timer = setTimeout(() => {
				setInterviewScheduleDrawer(false);
			}, 5000);

			return () => clearTimeout(timer);
		}
	}, [InterviewScheduleDrawer]);

	const Link = Quill.import('formats/link');
	class NonEditableLinkBlot extends Link {
		static create(value: string) {
			let node = super.create(value);
			node.setAttribute('href', value);
			node.setAttribute('contenteditable', 'false');
			node.classList.add('non-editable-link');
			return node;
		}
	}

	NonEditableLinkBlot.blotName = 'non-editable-link';
	NonEditableLinkBlot.tagName = 'A';
	Quill.register(NonEditableLinkBlot, true);

	const defaultContent = companyRole
		? `
			<div class="flex bg-[#FFF] rounded-[8px] justify-center">
				<div class="flex px-[20px] py-[13px] items-start gap-[10px] rounded-[8px] bg-[#F9FAFB]">
					<div class="font-inter font-[400] leading-[150%] text-[14px] text-[#555770]">
                        <div>
						Great news! You've been shortlisted to take an assessment for <br />
						<strong class="font-inter font-[700] leading-[150%] text-[14px] text-[#555770]">${companyRole}</strong> at
						<strong class="font-inter font-[700] leading-[150%] text-[14px] text-[#555770]">${user?.registeredCompany?.name}.
						</strong>
                        </div>
						<p>On successful submission of the test, our internal team will evaluate it and get back to you soon.</p>
						 
						<p>Take the assessment test from the following link: 
						<a href="https://levelup.masaischool.com/jobseeker/placed-hiring-assessment/${hiringAssessmentId}" class="cursor-pointer text-sm font-medium text-[#6698FA] break-all block pointer-events-none underline non-editable-link" contenteditable="false">
							https://levelup.masaischool.com/jobseeker/placed-hiring-assessment/${hiringAssessmentId}
						</a></p>
					    We wish you all the best!
					</div>
				</div>
			</div>
		`
		: '';

	useEffect(() => {
		setEmailContent(defaultContent);
	}, [defaultContent]);

	const [emailContent, setEmailContent] = useState(defaultContent);

	const handleSendInvites = (e: FormEvent) => {
		e.preventDefault();
		if (!emailContent.includes(hiringAssessmentUrl)) {
			toast({
				title: 'Please include the correct link in the email content',
				type: 'error',
			});
			return;
		}
		if (value.length === 0 && files.length === 0) {
			setEmailErrorMessage('Please enter emails or upload a CSV before proceeding.');
			return;
		}
		const emails = value.map(v => v.value);
		const invalidEmails = emails.filter(email => !validateEmailAddress(email));

		if (invalidEmails.length > 0) {
			setEmailErrorMessage('Invalid email addresses found');
			return;
		}

		const uniqueEmails = [...new Set(emails)];
		const duplicatedEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
		if (duplicatedEmails.length > 0) {
			toast({
				title: 'Duplicate emails found!',
				type: 'error',
			});
		}
		bulkActionFn.mutate({
			hiringAssessmentId: hiringAssessmentId || '',
			invitedEmails: uniqueEmails,
			templates: emailContent,
		});
		setEmailCount(emails.length);
		setValue([]);
		setInputValue('');
		setFiles([]);
	};

	return (
		<Container>
			<div className="flex items-center justify-center max-w-[580px] mx-auto my-4">
				<div className="bg-light-4 rounded-lg p-6 bg-white">
					<div className="mb-3">
						<p className="text-xl font-inter font-semibold text-[#1C1C28]">Your assessment is ready to share.</p>
					</div>
					<div className="flex flex-col items-start gap-4 rounded-lg pb-4 mt-2">
						<div className="flex items-center gap-4 rounded-lg w-full">
							<Avatar
								alt="companyLogo"
								src={avatarImg}
								sx={{ width: '51.2px', height: '51.2px', minWidth: '51.2px', minHeight: '51.2px', color: 'white' }}
							>
								{stringAvatar(
									user?.registeredCompany?.name.trim().toUpperCase() || user?.registeredCompany?.name || '',
								)}
							</Avatar>
							<div className="flex pr-2 flex-col items-start gap-1 w-full">
								<div className="text-[24px] font-semibold leading-[120%] text-dark-0 font-inter">
									{user?.registeredCompany?.name}
								</div>
								<div className="text-[16px] font-normal leading-[20px] text-[#28293D] font-inter">{companyRole}</div>
							</div>
						</div>
						<div className="flex items-start content-start gap-2 flex-wrap">
							<div className="flex gap-[4px]">
								<img src="/assets/recruiterImages/briefcase.svg" alt="Briefcase" className="max-w-4 w-full" />
								<span className="text-[#28293D] font-inter text-[12px] font-normal leading-[16px]">
									{
										getUserPreferenceUIConfig({ text: hiringAssessment?.userPreferenceSnapshot?.experienceLevel || '' })
											?.finalText
									}
								</span>
							</div>

							{!hiringAssessment?.userPreferenceSnapshot?.techStack?.includes(TechStackType.OTHER) ? (
								<>
									<span className="text-[#28293D] font-inter text-[12px] font-medium leading-[16px]">|</span>
									<div className="flex gap-[4px]">
										<img src="/assets/recruiterImages/tag.svg" alt="Tag" className="max-w-4 w-full" />
										<span className="text-[#28293D] font-inter text-[12px] font-medium leading-[16px]">
											{
												getUserPreferenceUIConfig({ text: hiringAssessment?.userPreferenceSnapshot?.techStack || '' })
													?.finalText
											}
										</span>
									</div>
								</>
							) : null}

							<div className="flex gap-[4px]">
								<span className="text-[#28293D] font-inter text-[12px] font-bold leading-[16px]">•</span>
								<span className="text-[#28293D] font-inter text-[12px] font-normal leading-[16px]">
									{hiringAssessment?.salaryRange?.min} - {hiringAssessment?.salaryRange?.max} LPA
								</span>
							</div>
						</div>
					</div>
					<div className="flex flex-col gap-[16px] pb-6">
						<div className="rounded-[12px] border border-[rgba(199,201,217,0.10)] bg-[#FFFEE5] flex items-center gap-2 px-[6px] py-[8px]">
							<img src="/assets/recruiterImages/exclamation-circle.svg" alt="Link" className="max-w-4 w-full" />
							<span className="text-[#555770] font-inter text-[14px] leading-[20px]">
								<strong>Note:</strong> Invited candidates will remain unavailable for other positions on Placed until
								the hiring process is complete.
							</span>
						</div>
					</div>
					<div className="flex flex-col gap-1.5 mb-8">
						<div className="flex w-full items-center border border-[#D6BBFB] bg-[#FFF] px-3 py-2 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_rgba(158,119,237,0.24)]">
							<div className="flex flex-1 items-center">
								<p className="text-[#101828] text-ellipsis font-inter">
									{`https://levelup.masaischool.com/jobseeker/placed-hiring-assessment/${hiringAssessmentId}`}
								</p>
							</div>
							<div className="flex justify-end">
								<Button size="xs" onClick={copyUrlToClipboard} color="purplePlaced" className="text-xs rounded-md">
									Copy Link
								</Button>
							</div>
						</div>
						<div>
							<p className="text-[#475467] font-inter text-sm text-center">
								This is your unique shareable link. You can invite candidates to take the assessment and evaluate their
								skills in depth.
							</p>
						</div>
					</div>

					<div className="flex justify-center items-center text-[#28293D] font-inter text-[14px] font-medium leading-[14px] pb-[15px] gap-[8px]">
						<hr className="border-t border-gray-300 w-full" />
						<span className="px-2">OR</span>
						<hr className="border-t border-gray-300 w-full" />
					</div>
					<div className="flex flex-col gap-[12px]">
						<div className="text-[#111928] font-inter text-[14px] font-[500] leading-[21px] flex justify-between items-center">
							<p>Message to candidates</p>
							{iseditableContent ? (
								<a
									href="#"
									className="text-[#7777E7] underline text-[13px] font-normal leading-[18px]"
									onClick={() => setEmailContent(defaultContent)}
								>
									Reset message
								</a>
							) : null}
						</div>

						{iseditableContent ? (
							<ReactQuill value={emailContent} onChange={setEmailContent} theme="snow" className="bg-[#F9FAFB]" />
						) : (
							<div className="p-[20px] items-start gap-[10px] rounded-[8px] border border-solid-[#D1D5DB] bg-[#F9FAFB] ">
								<div dangerouslySetInnerHTML={{ __html: emailContent }} />

								<div className="flex w-full justify-end">
									<Button
										className="border border-[#7777E7] shadow-[0px_2px_8px_0px_rgba(0,0,0,0.12)] mt-1"
										color="whiteBlue"
										onClick={() => {
											setIsEditableContent(!iseditableContent);
										}}
									>
										Edit Message
									</Button>
								</div>
							</div>
						)}

						{/* Hidden div with the text content to be copied */}
						<div
							ref={textRef}
							style={{
								position: 'absolute',
								left: '-9999px',
								fontFamily: 'Inter, sans-serif',
								fontSize: '14px',
								color: '#555770',
								lineHeight: '150%',
								whiteSpace: 'pre-wrap',
							}}
						>
							Great news! You've been shortlisted to take an assessment for {'\n'}
							<strong style={{ fontWeight: 700 }}>{companyRole.toString()}</strong> at{' '}
							<strong style={{ fontWeight: 700 }}>{user?.registeredCompany?.name}.</strong>
							{'\n\n'}
							On successful submission of the test, our internal team will evaluate it and get back to you soon.{'\n\n'}
							Take the assessment test from the following link: {'\n'}
							<a
								href={`https://levelup.masaischool.com/jobseeker/placed-hiring-assessment/${hiringAssessmentId}`}
								style={{ color: '#6698FA' }}
							>
								{`https://levelup.masaischool.com/jobseeker/placed-hiring-assessment/${hiringAssessmentId}`}
							</a>
							{'\n\n'}
							We wish you all the best!
						</div>
						<div className="flex flex-col gap-[12px]">
							{candidateAction ? (
								<>
									<div className="flex items-center gap-[8px]">
										<button
											className="flex justify-center items-center gap-[8px] bg-[#FFF]"
											onClick={() => setCandidateAction(false)}
										>
											<FaArrowLeft className="mr-1 min-h-[12px]" />
										</button>
										<p className="text-[#28293D] font-inter text-[14px] font-[500] leading-[20px]">
											Bulk upload candidates {value.length > 0 ? `(${value.length})` : ''}
										</p>
									</div>

									<div className="mb-2">
										{files.length > 0 ? (
											<>
												{/* <ul className="flex flex-wrap gap-2 p-3">
													{files.map((file, index) => (
														<li
															className="font-inter text-sm font-normal text-gray-500 text-center leading-[21px] flex items-center rounded-[32px] bg-[#E4E4EB]"
															key={index}
														>
															<span className="gap-1 p-2 pr-[4px]">{file.name}</span>
															<button
																type="button"
																className="text-gray-500 gap-1 h-full px-2 hover:bg-gray-200 rounded-r-[32px]"
																onClick={() => handleRemoveFile(index)}
															>
																<RxCross2 className="max-h-[20px] max-w-[20px] cursor-pointer text-[#28293D] text-xs" />
															</button>
														</li>
													))}
												</ul> */}
												<CreatableSelect
													isMulti
													value={value}
													onChange={handleChange}
													onInputChange={value => {
														setInputValue(value);
														// Call handleInputTextChange if the value contains spaces or commas
														if (value.includes(' ') || value.includes(',')) {
															handleInputTextChange(value);
														}
													}}
													onBlur={() => {
														if (inputValue) {
															handleInputTextChange(inputValue + ',');
														}
													}}
													className="max-w-[530px] w-full cursor-pointer break-all"
													classNames={{
														control: state => 'p-2 !bg-red-700',
													}}
													classNamePrefix="my-select-plans"
													inputValue={inputValue}
													placeholder="Paste in list of emails separated by comma. "
													menuIsOpen={false}
													onKeyDown={handleKeyDown}
													components={{ DropdownIndicator, IndicatorSeparator }}
													styles={{
														valueContainer: base => ({
															...base,
															maxHeight: '150px',
															overflowY: 'auto',
														}),
													}}
												/>
											</>
										) : (
											<form
												id="form-submit"
												className="w-full min-h-[107px] h-full border-2 border-dashed rounded-[8px] border-gray-200 bg-gray-50 cursor-pointer"
											>
												<label
													htmlFor="file"
													className={`${dragActive ? 'border-blue-500' : 'border-gray-300'
														} flex flex-col items-center gap-[5px] pt-[7px]`}
													onDragOver={handleDragOver}
													onDragLeave={handleDragLeave}
													onDrop={handleDrop}
												>
													<>
														<img src="/assets/cloud-upload.svg" alt="Upload" className="max-w-16" />
														{!files?.length ? (
															<input
																type="file"
																id="file"
																accept=".csv"
																className="hidden"
																onChange={handleFileChange}
															/>
														) : null}
														<span className="text-xs font-normal italic">
															<strong>Click to upload</strong> or drag and drop
														</span>
														<span className="text-gray-500 text-center font-inter text-xs font-normal leading-[18px]">
															CSV (MAX. 2MB)
														</span>
													</>
												</label>
											</form>
										)}
									</div>
								</>
							) : (
								<>
									<div className="mb-2 block whiteBgSelect">
										<div className="mb-[5px]">
											<Label className="text-[#111928] font-inter text-[14px] font-[500] leading-[21px]">
												{'Candidate email ids'}
											</Label>
											<span className="text-[red]">* </span>
										</div>
										<div className="text-[10px] mb-[4px] text-[red]">
											(Enter email separated by comma or space after each email id.)
										</div>
										<form onSubmit={handleSendInvites} id="form-submit" className="">
											<CreatableSelect
												isMulti
												value={value}
												onChange={handleChange}
												onInputChange={value => {
													setInputValue(value);
													// Call handleInputTextChange if the value contains spaces or commas
													if (value.includes(' ') || value.includes(',')) {
														handleInputTextChange(value);
													}
												}}
												onBlur={() => {
													if (inputValue) {
														handleInputTextChange(inputValue + ',');
													}
												}}
												className="max-w-[530px] w-full cursor-pointer break-all "
												classNames={{
													control: state => 'p-2 !bg-red-700',
												}}
												classNamePrefix="my-select-plans"
												inputValue={inputValue}
												placeholder="Paste in list of emails separated by comma. "
												menuIsOpen={false}
												onKeyDown={handleKeyDown}
												components={{ DropdownIndicator, IndicatorSeparator }}
												styles={{
													valueContainer: base => ({
														...base,
														maxHeight: '150px',
														overflowY: 'auto',
													}),
												}}
											/>
											{emailErrorMessage && <p className="text-red-500">{emailErrorMessage}</p>}
										</form>
									</div>
								</>
							)}

							<div className="flex justify-between items-center pb-[20px]">
								<div className="grid md:flex gap-[8px] w-full">
									<Button
										className="flex justify-center items-center gap-2 rounded-[8px] text-[#FFF] bg-[#7777E7] font-inter text-[14px] font-[500] leading-[21px]"
										color="purplePlaced"
										type="submit"
										onClick={handleSendInvites}
									>
										Send & Invite
									</Button>
								</div>

								<div className="flex justify-end max-w-[114] w-full items-center gap-[4px]">
									{!candidateAction ? (
										<>
											<BsCloudUploadFill className="text-[#7777E7]" />

											<button
												className="text-right text-[#7777E7] underline font-inter text-[13px] font-normal leading-[18px] overflow-hidden text-ellipsis whitespace-nowrap"
												onClick={handleCandidateAction}
											>
												Bulk upload csv
											</button>
										</>
									) : (
										<>
											<CSVDownloader
												filename="sample-emails"
												className="text-[#7777E7] font-inter text-[13px] font-normal leading-[18px] underline underline-offset-0"
												data={sampleData}
											>
												Download Sample CSV
											</CSVDownloader>
										</>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Drawer
				isOpen={InterviewScheduleDrawer}
				isModal={!isMobile}
				minHeight="fit-content"
				onClose={() => setInterviewScheduleDrawer(false)}
				header={<></>}
				body={<InterviewScheduledModal EmailCount={EmailCount} Role={companyRole} />}
			/>
			{/* <Drawer
				isOpen={CustomizedEmailDrawer}
				isModal={!isMobile}
				minHeight="fit-content"
				onClose={() => setCustomizedEmailDrawer(false)}
				header={<></>}
				body={
					<CustomizedEmailModal
						handleSubmit={handleSendInvites}
						setEmailContent={setEmailContent}
						emailContent={emailContent}
					/>
				}
			/> */}
		</Container>
	);
};
export default ShareHiringAssessment;
